import { ShieldCheck, Globe, ArrowRight } from "lucide-react";
import { Reveal, SectionTag, StatusPill } from "./Reveal";
import BrandMark from "./BrandMark";
import { scrollToId } from "@/App";

const USE_OF_FUNDS = [
  { label: "AI development", path: "/investment/use-of-funds/ai-development" },
  { label: "Avatar technology", path: "/investment/use-of-funds/avatar-technology" },
  { label: "Real-time video infrastructure", path: "/investment/use-of-funds/real-time-video-infrastructure" },
  { label: "Product development", path: "/investment/use-of-funds/product-development" },
  { label: "UX/UI", path: "/investment/use-of-funds/ux-ui" },
  { label: "Testing", path: "/investment/use-of-funds/testing" },
  { label: "Marketing", path: "/investment/use-of-funds/marketing" },
  { label: "Operations", path: "/investment/use-of-funds/operations" },
];

export default function Investment() {
  return (
    <section id="investment" data-testid="investment-section" className="relative overflow-hidden py-16 lg:py-36">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-cyan-400/[0.06] blur-[140px]" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-4">
            <BrandMark className="h-10 w-10 rounded-lg" testid="investment-logo" />
            <SectionTag>Investment</SectionTag>
          </div>
          <div className="mt-8 flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <span data-testid="raise-amount" className="font-display text-6xl font-extralight tracking-tighter text-white sm:text-7xl lg:text-8xl">
              £150,000
            </span>
            <span className="font-display text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">
              Fundraising Round
            </span>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <StatusPill tone="cyan">Fundraising preparation</StatusPill>
            <StatusPill tone="cyan">Seeking investment</StatusPill>
          </div>
          <p className="font-display mt-8 text-[11px] font-medium uppercase tracking-[0.3em] text-white/70">
            Proposed round terms
          </p>
          <div data-testid="round-terms" className="mt-3 flex flex-wrap gap-x-10 gap-y-4">
            {[
              ["£600,000", "Pre-money valuation"],
              ["£750,000", "Post-money valuation"],
              ["20%", "Equity across fully subscribed round"],
            ].map(([value, label]) => (
              <div key={label}>
                <p className="font-display text-xl font-medium tracking-tight text-white">{value}</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/55">{label}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#C5CBD5]">
            AIROBOTCAMSTUDIO LTD is preparing to raise £150,000 to accelerate development of its
            real-time AI videochat MVP and commercial platform. Level 1 — the AI video companion
            platform — is the current commercial build, and the round is focused on completing and
            validating it.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="font-display mt-14 text-[11px] font-medium uppercase tracking-[0.35em] text-white/70">
            Use of funds
          </p>
          <div data-testid="funds-allocation" className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["£85,000", "MVP & AI development / directly related R&D", "56.7%"],
              ["£35,000", "Go-to-market & controlled acquisition", "23.3%"],
              ["£20,000", "Infrastructure, legal, compliance & operations", "13.3%"],
              ["£10,000", "Working capital", "6.7%"],
            ].map(([value, label, pct]) => (
              <div key={label} className="rounded-xl border border-white/10 bg-white/[0.03] p-6">
                <p className="font-display text-2xl font-light tracking-tight text-white">{value}</p>
                <p className="mt-2 min-h-[32px] text-xs leading-relaxed text-[#C5CBD5]">{label}</p>
                <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300/80">{pct}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 max-w-3xl text-xs leading-relaxed text-white/70">
            Total £150,000. Capital is focused entirely on completing, validating and launching the
            AI video companion platform.
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <p className="font-display mt-14 text-[11px] font-medium uppercase tracking-[0.35em] text-white/70">
            Development breakdown
          </p>
          <p className="mt-3 max-w-2xl text-xs leading-relaxed text-white/70">
            The detailed development areas funded within these allocations.
          </p>
          <div data-testid="use-of-funds" className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {USE_OF_FUNDS.map(({ label, path }) => (
              <a
                key={label}
                href={path}
                data-testid={`use-of-funds-${path.split("/").pop()}`}
                className="group rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3.5 transition-colors duration-300 hover:border-cyan-300/40"
              >
                <span className="flex items-center gap-3 text-[13px] font-medium text-white/75 transition-colors duration-300 group-hover:text-white">
                  <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-cyan-300/70" />
                  {label}
                </span>
                <span className="mt-1.5 flex items-center gap-1.5 pl-[18px] text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-300/80 transition-colors duration-300 group-hover:text-cyan-100">
                  View allocation
                  <ArrowRight size={11} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </span>
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="robotics-future-note" className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8">
            <p className="font-display text-[11px] font-semibold uppercase tracking-[0.3em] text-white/70">
              Future vision · Level 4+
            </p>
            <h3 className="font-display mt-3 text-lg font-medium uppercase tracking-[0.12em] text-white">
              Humanoid AI &amp; Robotics
            </h3>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#C5CBD5]">
              Humanoid robotics forms part of AIROBOTCAMSTUDIO&rsquo;s longer-term technology vision. It is
              not included in the current £150,000 MVP fundraising allocation. Robotics research, hardware
              development and physical humanoid integration are intended for a later stage of the
              company&rsquo;s development and would be subject to separate planning and funding.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
              <StatusPill tone="amber">Not part of the current £150,000 MVP fundraising allocation</StatusPill>
              <a
                href="/roadmap/humanoid-ai"
                data-testid="robotics-vision-link"
                className="group inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.15em] text-cyan-300/90 transition-colors duration-300 hover:text-cyan-100"
              >
                Explore the long-term vision
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-4 lg:grid-cols-2">
          <Reveal delay={0.1}>
            <div data-testid="seis-card" className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 lg:p-10">
              <div className="flex items-center justify-between gap-4">
                <ShieldCheck size={22} className="text-cyan-300/80" />
                <StatusPill tone="amber">Application being prepared</StatusPill>
              </div>
              <h3 className="font-display mt-6 text-xl font-medium uppercase tracking-[0.15em] text-white">
                SEIS Advance Assurance
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-[#C5CBD5]">
                AIROBOTCAMSTUDIO LTD is preparing an application for SEIS Advance Assurance. SEIS
                eligibility and investor tax relief are subject to HMRC approval and applicable
                requirements.
              </p>
              <a
                href="/investment/seis-advance-assurance"
                data-testid="seis-link"
                className="group mt-6 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.15em] text-cyan-300/90 transition-colors duration-300 hover:text-cyan-100"
              >
                Learn about our SEIS process
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div data-testid="crowdcube-card" className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-8 lg:p-10">
              <Globe size={22} className="text-cyan-300/80" />
              <h3 className="font-display mt-6 text-xl font-medium uppercase tracking-[0.15em] text-white">
                Preparing for Crowdcube
              </h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-[#C5CBD5]">
                AIROBOTCAMSTUDIO LTD is preparing for a future Crowdcube fundraising campaign as
                part of its wider investment strategy.
              </p>
              <a
                href="/investment/crowdcube"
                data-testid="crowdcube-link"
                className="group mt-6 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.15em] text-cyan-300/90 transition-colors duration-300 hover:text-cyan-100"
              >
                How Crowdcube fundraising works
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <button
                data-testid="crowdcube-register-btn"
                onClick={() => scrollToId("contact")}
                className="mt-6 w-fit rounded-md bg-cyan-300 px-6 py-3 text-sm font-bold text-black transition-transform duration-300 hover:-translate-y-1 hover:bg-cyan-200"
              >
                Register Investor Interest
              </button>
              <p className="mt-4 text-[11px] leading-relaxed text-white/55">
                Registering interest with AIROBOTCAMSTUDIO is not an investment commitment and is
                separate from registering or investing through Crowdcube.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.18}>
          <div data-testid="investment-risks" className="mt-14 rounded-2xl border border-white/10 bg-white/[0.02] p-8">
            <p className="font-display text-[11px] font-semibold uppercase tracking-[0.3em] text-white/70">
              Key risk considerations
            </p>
            <div className="mt-5 grid gap-6 sm:grid-cols-2">
              {[
                ["Product / Compliance", "Moderation, privacy, age controls and legal advice."],
                ["Technology", "Measured development, latency testing and scalable infrastructure."],
                ["Market Adoption", "MVP validation and data-driven iteration."],
                ["Unit Economics", "CAC, retention and infrastructure cost per streamed minute."],
              ].map(([title, text]) => (
                <div key={title}>
                  <p className="text-sm font-semibold text-white/85">{title}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-[#C5CBD5]">{text}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-xs leading-relaxed text-white/70">
              These are active areas of work, not solved problems. SEIS capital is genuinely at
              risk, including the potential loss of capital.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.19}>
          <div className="mt-14 text-center">
            <button
              data-testid="investment-primary-cta"
              onClick={() => scrollToId("invest-now")}
              className="rounded-md bg-cyan-300 px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-black transition-transform duration-300 hover:-translate-y-1 hover:bg-cyan-200 active:scale-[0.98]"
            >
              Register Investor Interest
            </button>
            <p data-testid="investment-cta-clarification" className="mx-auto mt-4 max-w-xl text-[11px] leading-relaxed text-white/55">
              Registering investor interest is not an investment, a subscription for shares or a
              binding commitment.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-12 max-w-3xl text-xs leading-relaxed text-white/55">
            This page is provided for information purposes only and does not constitute an offer of
            securities or investment advice. The fundraising round is in preparation and no
            investment has been secured. Any investment in early-stage companies carries risk,
            including the potential loss of capital.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
